import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Sparkles,
  Users,
  Building2,
  Car,
  ScanLine,
  Package,
  Pencil,
  FileText,
  Download,
  CheckCircle2,
  Send,
  AlertTriangle,
  Loader2,
  Lock,
  Unlock,
  ShieldAlert,
  Mailbox,
  Mail,
  CircleDot,
  Reply,
  RotateCcw,
  Bell,
  Printer,
  X,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import StickerOrderEditModal from "@/components/StickerOrderEditModal";

const STATUS_PILL = {
  pending: "bg-[#FEF3C7] text-[#92400E] border-[#F59E0B]/30",
  sent: "bg-[#DBEAFE] text-[#1E40AF] border-[#3B82F6]/30",
  paid: "bg-[#0F8F4F]/15 text-[#0F8F4F] border-[#0F8F4F]/30",
  overdue: "bg-[#FEE2E2] text-[#B91C1C] border-[#EF4444]/30",
  void: "bg-[#F4F4F5] text-[#71717A] border-[#E4E4E7]",
};

export default function SuperAdmin() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [stickerOrders, setStickerOrders] = useState([]);
  const [editing, setEditing] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [suspended, setSuspended] = useState([]);
  const [lockedAccounts, setLockedAccounts] = useState([]);
  const [unlockingEmail, setUnlockingEmail] = useState(null);
  const [editingInv, setEditingInv] = useState(null);
  const [leads, setLeads] = useState([]);
  const [leadFilter, setLeadFilter] = useState("all"); // all | new | contacted
  const [leadUpdating, setLeadUpdating] = useState(null);
  const [exportingCsv, setExportingCsv] = useState(false);
  // Drill-down state — clicking a stat card opens this side panel with
  // the full underlying list for that metric.
  const [detailPanel, setDetailPanel] = useState(null); // 'users' | 'properties' | 'paid' | 'vehicles' | 'invoices' | null
  const [detailUserId, setDetailUserId] = useState(null);
  const [detailUser, setDetailUser] = useState(null);
  const [detailUserLoading, setDetailUserLoading] = useState(false);
  // Purge preview + confirmation state
  const [purgePreview, setPurgePreview] = useState(null);
  const [purging, setPurging] = useState(false);
  const [purgeConfirmText, setPurgeConfirmText] = useState("");
  const [adminNotifs, setAdminNotifs] = useState([]);
  const [notifsUnread, setNotifsUnread] = useState(0);
  const [notifBusy, setNotifBusy] = useState(null);

  const load = async () => {
    try {
      const [s, u, o, inv, susp, locks, ld, notifs] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/users"),
        api.get("/admin/sticker-orders"),
        api.get("/admin/invoices"),
        api.get("/admin/suspended-properties"),
        api.get("/admin/login-attempts"),
        api.get("/admin/leads/pricing-estimates?limit=500"),
        api.get("/admin/notifications?limit=50"),
      ]);
      setStats(s.data);
      setUsers(u.data || []);
      setStickerOrders(o.data || []);
      setInvoices(inv.data || []);
      setSuspended(susp.data || []);
      setLockedAccounts(locks.data || []);
      setLeads(ld.data?.leads || []);
      setAdminNotifs(notifs.data?.rows || []);
      setNotifsUnread(notifs.data?.unread_count || 0);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const markNotifRead = async (nid) => {
    setNotifBusy(nid);
    try {
      await api.post(`/admin/notifications/${nid}/read`);
      setAdminNotifs((rows) =>
        rows.map((r) => (r.id === nid ? { ...r, read_at: new Date().toISOString() } : r))
      );
      setNotifsUnread((n) => Math.max(0, n - 1));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setNotifBusy(null);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const seed = async () => {
    if (!window.confirm("Seed demo data? Existing demo property will be refreshed.")) return;
    setSeeding(true);
    try {
      const { data } = await api.post("/admin/seed-demo");
      toast.success(
        `Seeded ${data.tenants_created} tenants, ${data.vehicles_created} vehicles`
      );
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSeeding(false);
    }
  };

  const sweepNow = async () => {
    try {
      const { data } = await api.post("/admin/sweep-suspensions");
      toast.success(
        `Sweep complete · ${data.suspended_properties} properties suspended`
      );
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const downloadInvoicePdf = async (inv) => {
    try {
      const resp = await api.get(`/billing/invoices/${inv.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${inv.invoice_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    }  };

  const toggleLeadStatus = async (lead) => {
    const next = lead.status === "contacted" ? "new" : "contacted";
    setLeadUpdating(lead.id);
    try {
      const { data } = await api.patch(
        `/admin/leads/pricing-estimates/${lead.id}`,
        { status: next }
      );
      setLeads((rows) => rows.map((r) => (r.id === lead.id ? data : r)));
      toast.success(
        next === "contacted"
          ? `Marked ${lead.email} as contacted`
          : `Reset ${lead.email} to new`
      );
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLeadUpdating(null);
    }
  };

  const exportLeadsCsv = async () => {
    setExportingCsv(true);
    try {
      const resp = await api.get("/admin/leads/pricing-estimates.csv", {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(
        new Blob([resp.data], { type: "text/csv" })
      );
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `scanmint-pricing-leads-${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setExportingCsv(false);
    }
  };

  const openPurgeDialog = async () => {
    setPurging(true);
    setPurgeConfirmText("");
    try {
      const { data } = await api.post("/admin/purge-test-data", { dry_run: true });
      setPurgePreview(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPurging(false);
    }
  };

  const runPurge = async () => {
    if (purgeConfirmText !== "PURGE") {
      toast.error("Type PURGE (uppercase) to confirm");
      return;
    }
    setPurging(true);
    try {
      const { data } = await api.post("/admin/purge-test-data", {
        dry_run: false,
        confirm: "PURGE",
      });
      toast.success(`Deleted ${data.deleted?.users || 0} test users + cascades`);
      setPurgePreview(null);
      setPurgeConfirmText("");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPurging(false);
    }
  };

  const openUserDetail = async (userId) => {
    setDetailUserId(userId);
    setDetailUser(null);
    setDetailUserLoading(true);
    try {
      const { data } = await api.get(`/admin/users/${userId}`);
      setDetailUser(data);
    } catch (e) {
      toast.error(formatApiError(e));
      setDetailUserId(null);
    } finally {
      setDetailUserLoading(false);
    }
  };

  const sendLeadFollowUp = async (lead) => {
    const already = (lead.follow_up_count || 0) > 0;
    const confirmMsg = already
      ? `Resend the follow-up email to ${lead.email}?`
      : `Send the follow-up email to ${lead.email}?`;
    if (!window.confirm(confirmMsg)) return;
    setLeadUpdating(lead.id);
    try {
      const { data } = await api.post(
        `/admin/leads/pricing-estimates/${lead.id}/follow-up`
      );
      setLeads((rows) => rows.map((r) => (r.id === lead.id ? data.lead : r)));
      toast.success(
        `Follow-up ${data.notification_status === "sent_mock" ? "logged (mock)" : "sent"} to ${lead.email}`
      );
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLeadUpdating(null);
    }
  };

  const resetLeadReply = async (lead) => {
    if (
      !window.confirm(
        `Clear the 'Replied' flag on ${lead.email}? Use this if the reply was a bounce or auto-responder.`
      )
    )
      return;
    setLeadUpdating(lead.id);
    try {
      const { data } = await api.post(
        `/admin/leads/pricing-estimates/${lead.id}/reset-reply`
      );
      setLeads((rows) => rows.map((r) => (r.id === lead.id ? data : r)));
      toast.success(`Reply flag cleared for ${lead.email}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLeadUpdating(null);
    }
  };

  const sendLeadQuickReply = async (lead, payload) => {
    setLeadUpdating(lead.id);
    try {
      const { data } = await api.post(
        `/admin/leads/pricing-estimates/${lead.id}/quick-reply`,
        payload
      );
      setLeads((rows) => rows.map((r) => (r.id === lead.id ? data.lead : r)));
      toast.success(
        `Reply ${data.notification_status === "sent_mock" ? "logged (mock)" : "sent"} to ${lead.email}`
      );
      return true;
    } catch (e) {
      toast.error(formatApiError(e));
      return false;
    } finally {
      setLeadUpdating(null);
    }
  };

  const quickStatus = async (inv, status) => {
    try {
      await api.patch(`/billing/invoices/${inv.id}`, { status });
      toast.success(`Invoice ${inv.invoice_number} marked ${status}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const unlockEmail = async (email) => {
    if (!window.confirm(`Unlock ${email}?\n\nThis clears their failed-attempt counter and removes any active 15-minute lock.`)) return;
    setUnlockingEmail(email);
    try {
      const { data } = await api.post("/admin/login-attempts/clear", { email });
      toast.success(`${email} unlocked · ${data.cleared} record${data.cleared === 1 ? "" : "s"} cleared`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUnlockingEmail(null);
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="mono-tiny text-[#71717A]">Super Admin</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-2">
            Platform console
          </h1>
          <p className="text-[#71717A] text-sm mt-1">
            Global stats, invoice approvals, and suspension controls.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            onClick={sweepNow}
            variant="outline"
            className="h-12"
            data-testid="admin-sweep-now"
          >
            <AlertTriangle className="w-4 h-4 mr-2" /> Run sweep now
          </Button>
          <Button
            onClick={seed}
            disabled={seeding}
            className="bg-[#09090B] hover:bg-[#09090B]/90 h-12 text-white"
            data-testid="admin-seed-demo"
          >
            <Sparkles className="w-4 h-4 mr-2" /> {seeding ? "Seeding…" : "Seed demo data"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <AdminStat
          k="users"
          label="Users"
          icon={Users}
          stats={stats}
          onClick={() => setDetailPanel("users")}
          hint={stats && stats.test_users > 0 ? `${stats.real_users} real · ${stats.test_users} test` : null}
        />
        <AdminStat
          k="properties"
          label="Properties"
          icon={Building2}
          stats={stats}
          onClick={() => setDetailPanel("properties")}
        />
        <AdminStat
          k="paid_accounts"
          label="Paid accounts"
          icon={CheckCircle2}
          stats={stats}
          onClick={() => setDetailPanel("paid")}
          accent
        />
        <AdminStat
          k="vehicles"
          label="Vehicles"
          icon={Car}
          stats={stats}
          onClick={() => setDetailPanel("vehicles")}
          hint={stats ? `${stats.active_vehicles ?? 0} active` : null}
        />
        <AdminStat
          k="paid_invoices"
          label="Paid invoices"
          icon={FileText}
          stats={stats}
          onClick={() => setDetailPanel("invoices")}
          hint={stats && stats.overdue_invoices > 0 ? `${stats.overdue_invoices} overdue` : null}
        />
        <AdminStat
          k="scan_logs"
          label="Scans"
          icon={ScanLine}
          stats={stats}
        />
      </div>

      {stats && stats.test_users > 0 && (
        <div
          className="bg-[#FEF3C7] border border-[#F59E0B]/40 rounded-lg p-4 flex flex-col md:flex-row md:items-center gap-3 md:gap-4"
          data-testid="admin-test-data-banner"
        >
          <AlertTriangle className="w-5 h-5 text-[#92400E] shrink-0" strokeWidth={1.75} />
          <div className="flex-1 text-sm text-[#78350F]">
            <b>{stats.test_users} test users</b> detected (auto-generated by QA runs).
            Purge them + all their properties, invoices, and vehicles to reset the dashboard.
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={openPurgeDialog}
            disabled={purging}
            className="bg-white border-[#F59E0B]/60 text-[#78350F] hover:bg-[#FEF3C7]"
            data-testid="admin-purge-open"
          >
            {purging ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <AlertTriangle className="w-4 h-4 mr-2" />}
            Review &amp; purge test data
          </Button>
        </div>
      )}

      {/* === Admin notifications inbox ===
          Populated by paid sticker-reprint requests today; primed for
          any future _admin_notify() call sites (e.g. enterprise quote
          bumps, refund requests, escalations). Unread count drives the
          badge in the header. */}
      <div
        className="bg-white border border-[#E4E4E7] rounded-lg overflow-hidden"
        data-testid="admin-notifications-card"
      >
        <div className="px-5 py-4 border-b border-[#E4E4E7] flex items-center gap-2">
          <Bell className="w-4 h-4 text-[#0F8F4F]" strokeWidth={1.5} />
          <div className="mono-tiny text-[#71717A]">
            Admin notifications ({adminNotifs.length})
          </div>
          {notifsUnread > 0 && (
            <span
              className="ml-2 px-2 py-0.5 rounded-full text-xs font-semibold bg-[#EF4444] text-white"
              data-testid="admin-notifications-unread-badge"
            >
              {notifsUnread} unread
            </span>
          )}
        </div>
        {adminNotifs.length === 0 ? (
          <div className="p-6 text-sm text-[#71717A]" data-testid="admin-notifications-empty">
            Inbox is clear — paid sticker reprint requests, escalations, and
            enterprise-quote pings will land here.
          </div>
        ) : (
          <div className="divide-y divide-[#E4E4E7] max-h-[420px] overflow-y-auto">
            {adminNotifs.map((n) => {
              const unread = !n.read_at;
              const isReprint = n.type === "sticker_reprint";
              return (
                <div
                  key={n.id}
                  className={`p-4 md:p-5 flex items-start gap-3 ${
                    unread ? "bg-[#FFFBEB]" : "bg-white"
                  }`}
                  data-testid={`admin-notif-row-${n.id}`}
                >
                  <div
                    className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                      isReprint
                        ? "bg-[#0F8F4F]/10 text-[#0F8F4F]"
                        : "bg-[#E4E4E7] text-[#3F3F46]"
                    }`}
                  >
                    {isReprint ? (
                      <Printer className="w-4 h-4" strokeWidth={1.5} />
                    ) : (
                      <Bell className="w-4 h-4" strokeWidth={1.5} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-medium text-[#09090B]">{n.title}</div>
                      {unread && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase bg-[#EF4444]/10 text-[#B91C1C] border border-[#EF4444]/30">
                          NEW
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-[#3F3F46] mt-1">{n.body}</div>
                    <div className="mono-tiny text-[#71717A] mt-2">
                      {new Date(n.created_at).toLocaleString()}
                      {n.meta?.amount ? ` · $${Number(n.meta.amount).toFixed(2)}` : ""}
                    </div>
                  </div>
                  {unread && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 shrink-0"
                      onClick={() => markNotifRead(n.id)}
                      disabled={notifBusy === n.id}
                      data-testid={`admin-notif-read-${n.id}`}
                    >
                      {notifBusy === n.id ? (
                        <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />
                      ) : (
                        <CheckCircle2 className="w-3 h-3 mr-1.5" strokeWidth={1.5} />
                      )}
                      Mark read
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* === Suspended properties === */}
      <div
        className="bg-white border border-[#E4E4E7] rounded-lg p-6"
        data-testid="admin-suspended-card"
      >
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle className="w-4 h-4 text-[#EF4444]" strokeWidth={1.5} />
          <div className="mono-tiny text-[#71717A]">
            Suspended properties ({suspended.length})
          </div>
        </div>
        {suspended.length === 0 ? (
          <div className="text-sm text-[#71717A] py-3">
            No properties currently suspended.
          </div>
        ) : (
          <div className="divide-y divide-[#E4E4E7]">
            {suspended.map((p) => (
              <div
                key={p.id}
                className="py-3 flex items-center justify-between gap-3 flex-wrap"
                data-testid={`admin-suspended-${p.id}`}
              >
                <div>
                  <div className="font-medium">{p.property_name}</div>
                  <div className="mono-tiny text-[#71717A]">
                    {p.corporation_name} · suspended {new Date(p.billing_suspended_at).toLocaleDateString()}
                  </div>
                </div>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border bg-[#FEE2E2] text-[#B91C1C] border-[#EF4444]/30">
                  PAYMENT REQUIRED
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* === Locked accounts === */}
      <LockedAccountsCard
        rows={lockedAccounts}
        onUnlock={unlockEmail}
        unlockingEmail={unlockingEmail}
      />

      {/* === Pricing-calculator leads (CRM) === */}
      <LeadsCard
        rows={leads}
        filter={leadFilter}
        onFilter={setLeadFilter}
        onToggle={toggleLeadStatus}
        onFollowUp={sendLeadFollowUp}
        onResetReply={resetLeadReply}
        onQuickReply={sendLeadQuickReply}
        onExport={exportLeadsCsv}
        updatingId={leadUpdating}
        exporting={exportingCsv}
      />

      {/* === Invoices === */}
      <div
        className="bg-white border border-[#E4E4E7] rounded-lg overflow-hidden"
        data-testid="admin-invoices-card"
      >
        <div className="flex items-center gap-2 p-5 border-b border-[#E4E4E7]">
          <FileText className="w-4 h-4 text-[#0F8F4F]" strokeWidth={1.5} />
          <div className="mono-tiny text-[#71717A]">
            All invoices ({invoices.length})
          </div>
        </div>
        {invoices.length === 0 ? (
          <div className="p-6 text-sm text-[#71717A]">No invoices yet.</div>
        ) : (
          <div className="divide-y divide-[#E4E4E7]">
            {invoices.map((inv) => (
              <div
                key={inv.id}
                className="p-4 md:p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-3"
                data-testid={`admin-invoice-${inv.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium font-mono">{inv.invoice_number}</span>
                    <span
                      className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${
                        STATUS_PILL[inv.status] || STATUS_PILL.pending
                      }`}
                    >
                      {inv.status}
                    </span>
                    {inv.property_suspended && (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border bg-[#FEE2E2] text-[#B91C1C] border-[#EF4444]/30">
                        SUSPENDED
                      </span>
                    )}
                  </div>
                  <div className="mono-tiny text-[#71717A] mt-1">
                    {inv.property_name} · {inv.corporation_name}
                  </div>
                  <div className="mono-tiny text-[#71717A]">
                    Issued {new Date(inv.issued_at).toLocaleDateString()} · Due {new Date(inv.due_date).toLocaleDateString()}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-display font-bold text-lg" data-testid={`admin-invoice-total-${inv.id}`}>
                    ${(inv.total || 0).toFixed(2)}
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => downloadInvoicePdf(inv)}
                    data-testid={`admin-invoice-pdf-${inv.id}`}
                  >
                    <Download className="w-3.5 h-3.5 mr-1.5" /> PDF
                  </Button>
                  {inv.status !== "sent" && inv.status !== "paid" && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => quickStatus(inv, "sent")}
                      data-testid={`admin-invoice-mark-sent-${inv.id}`}
                    >
                      <Send className="w-3.5 h-3.5 mr-1.5" /> Sent
                    </Button>
                  )}
                  {inv.status !== "paid" && (
                    <Button
                      size="sm"
                      onClick={() => quickStatus(inv, "paid")}
                      className="bg-[#0F8F4F] hover:bg-[#0A6B3B]"
                      data-testid={`admin-invoice-mark-paid-${inv.id}`}
                    >
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Paid
                    </Button>
                  )}
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => setEditingInv(inv)}
                    data-testid={`admin-invoice-edit-${inv.id}`}
                  >
                    <Pencil className="w-4 h-4" strokeWidth={1.5} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white border border-[#E4E4E7] rounded-lg p-6">
        <div className="flex items-center gap-2 mb-3">
          <Package className="w-4 h-4 text-[#0F8F4F]" strokeWidth={1.5} />
          <div className="mono-tiny text-[#71717A]">Sticker orders ({stickerOrders.length})</div>
        </div>
        {stickerOrders.length === 0 ? (
          <div className="text-sm text-[#71717A] py-3">No orders yet.</div>
        ) : (
          <div className="divide-y divide-[#E4E4E7]">
            {stickerOrders.map((o) => (
              <div
                key={o.id}
                className="py-3 flex items-center justify-between gap-3"
                data-testid={`admin-order-${o.id}`}
              >
                <div className="min-w-0">
                  <div className="font-medium">
                    {o.property_name || "—"} · {o.quantity} pack{o.quantity > 1 ? "s" : ""}
                  </div>
                  <div className="mono-tiny text-[#71717A]">
                    {new Date(o.created_at).toLocaleString()} ·{" "}
                    {o.shipping?.full_name || "—"}, {o.shipping?.city || "—"}
                  </div>
                  {o.tracking_number && (
                    <div className="mono-tiny text-[#0F8F4F] mt-0.5">
                      {o.carrier ? `${o.carrier} · ` : ""}{o.tracking_number}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="mono-tiny text-[#71717A] uppercase">
                    {o.order_status || "processing"}
                  </span>
                  <span className="font-display font-bold text-sm">
                    {o.amount === 0 ? "FREE" : `$${o.amount.toFixed(2)}`}
                  </span>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => setEditing(o)}
                    data-testid={`admin-edit-order-${o.id}`}
                  >
                    <Pencil className="w-4 h-4" strokeWidth={1.5} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white border border-[#E4E4E7] rounded-lg p-6">
        <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
          <div className="mono-tiny text-[#71717A]">All users ({users.length})</div>
          <div className="mono-tiny text-[#A1A1AA]">Click a row for full details</div>
        </div>
        <div className="divide-y divide-[#E4E4E7]">
          {users.map((u) => (
            <button
              type="button"
              key={u.id}
              onClick={() => openUserDetail(u.id)}
              className="w-full py-3 flex items-center justify-between text-left hover:bg-[#FAFAFA] rounded transition-colors px-2 -mx-2"
              data-testid={`admin-user-${u.id}`}
            >
              <div className="min-w-0">
                <div className="font-medium truncate">{u.name}</div>
                <div className="mono-tiny text-[#71717A] truncate">{u.email}</div>
              </div>
              <div className="mono-tiny text-[#71717A] capitalize shrink-0 ml-3">
                {u.role.replace("_", " ")}
              </div>
            </button>
          ))}
        </div>
      </div>

      {editing && (
        <StickerOrderEditModal
          order={editing}
          open={!!editing}
          onClose={() => setEditing(null)}
          onSaved={() => load()}
        />
      )}

      {detailUserId && (
        <UserDetailModal
          open={!!detailUserId}
          data={detailUser}
          loading={detailUserLoading}
          onMarkSetupPaid={async (pid) => {
            if (
              !window.confirm(
                "Mark the $99 setup fee paid for this property? Any pending vehicles will activate immediately."
              )
            )
              return;
            try {
              await api.post(`/admin/properties/${pid}/mark-setup-paid`);
              toast.success("Setup fee marked paid · QRs activated");
              await openUserDetail(detailUserId);
              await load();
            } catch (e) {
              toast.error(formatApiError(e));
            }
          }}
          onClearLateFee={async (iid) => {
            if (
              !window.confirm(
                "Remove the late fee and reset this invoice's total to setup + monthly only?"
              )
            )
              return;
            try {
              const { data } = await api.post(
                `/admin/invoices/${iid}/clear-late-fee`,
              );
              toast.success(
                `Invoice reset · new total $${data.new_total.toFixed(2)}`,
              );
              await openUserDetail(detailUserId);
              await load();
            } catch (e) {
              toast.error(formatApiError(e));
            }
          }}
          onClose={() => {
            setDetailUserId(null);
            setDetailUser(null);
          }}
        />
      )}

      {purgePreview && (
        <PurgeTestDataDialog
          preview={purgePreview}
          confirmText={purgeConfirmText}
          setConfirmText={setPurgeConfirmText}
          onConfirm={runPurge}
          onCancel={() => {
            setPurgePreview(null);
            setPurgeConfirmText("");
          }}
          busy={purging}
        />
      )}

      {detailPanel && (
        <StatsDrillDownModal
          which={detailPanel}
          stats={stats}
          users={users}
          properties={[]}
          suspended={suspended}
          invoices={invoices}
          onOpenUser={openUserDetail}
          onClose={() => setDetailPanel(null)}
        />
      )}

      {editingInv && (
        <InvoiceEditDialog
          invoice={editingInv}
          onClose={() => setEditingInv(null)}
          onSaved={() => {
            setEditingInv(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function InvoiceEditDialog({ invoice, onClose, onSaved }) {
  const [status, setStatus] = useState(invoice.status || "pending");
  const [mailedAt, setMailedAt] = useState(invoice.mailed_at?.slice(0, 10) || "");
  const [adminNotes, setAdminNotes] = useState(invoice.admin_notes || "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const body = { status, admin_notes: adminNotes };
      if (mailedAt) body.mailed_at = new Date(mailedAt).toISOString();
      await api.patch(`/billing/invoices/${invoice.id}`, body);
      toast.success(`Invoice ${invoice.invoice_number} updated`);
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="admin-invoice-edit-dialog">
        <DialogHeader>
          <DialogTitle>Edit invoice {invoice.invoice_number}</DialogTitle>
          <DialogDescription>
            {invoice.property_name} · ${(invoice.total || 0).toFixed(2)}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="mono-tiny text-[#71717A]">Status</Label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full h-10 mt-1 border border-[#E4E4E7] rounded-md px-3 bg-white"
              data-testid="admin-invoice-edit-status"
            >
              <option value="pending">Pending</option>
              <option value="sent">Sent</option>
              <option value="paid">Paid</option>
              <option value="overdue">Overdue</option>
              <option value="void">Void</option>
            </select>
          </div>
          <div>
            <Label className="mono-tiny text-[#71717A]">Mailed date</Label>
            <Input
              type="date"
              value={mailedAt}
              onChange={(e) => setMailedAt(e.target.value)}
              className="h-10 mt-1"
              data-testid="admin-invoice-edit-mailed"
            />
          </div>
          <div>
            <Label className="mono-tiny text-[#71717A]">Admin notes</Label>
            <textarea
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              rows={3}
              className="w-full mt-1 border border-[#E4E4E7] rounded-md px-3 py-2 text-sm"
              data-testid="admin-invoice-edit-notes"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            onClick={save}
            disabled={busy}
            className="bg-[#0F8F4F] hover:bg-[#0A6B3B]"
            data-testid="admin-invoice-edit-save"
          >
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AdminStat({ k, label, icon: Icon, stats, onClick, hint, accent }) {
  const isClickable = typeof onClick === "function";
  const Wrapper = isClickable ? "button" : "div";
  return (
    <Wrapper
      onClick={onClick}
      type={isClickable ? "button" : undefined}
      className={`text-left bg-white border rounded-lg p-5 transition-colors w-full ${
        accent
          ? "border-[#0F8F4F]/40 hover:border-[#0F8F4F]"
          : "border-[#E4E4E7] hover:border-[#0F8F4F]/50"
      } ${isClickable ? "cursor-pointer hover:shadow-sm" : ""}`}
      data-testid={`admin-stat-${k}`}
    >
      <Icon
        className={`w-4 h-4 ${accent ? "text-[#0F8F4F]" : "text-[#0F8F4F]"}`}
        strokeWidth={1.5}
      />
      <div className="mono-tiny text-[#71717A] mt-3">{label}</div>
      <div className="font-display text-3xl font-bold mt-1">
        {stats ? stats[k] ?? 0 : "—"}
      </div>
      {hint ? (
        <div className="mono-tiny text-[#71717A] mt-1">{hint}</div>
      ) : null}
    </Wrapper>
  );
}


function LockedAccountsCard({ rows, onUnlock, unlockingEmail }) {
  // Locked first, then high-failure-count emails, newest activity first.
  const sorted = [...(rows || [])].sort((a, b) => {
    if (a.currently_locked !== b.currently_locked) {
      return a.currently_locked ? -1 : 1;
    }
    return (b.last_attempt_at || "").localeCompare(a.last_attempt_at || "");
  });
  const lockedCount = sorted.filter((r) => r.currently_locked).length;

  const formatRemaining = (lockedUntil) => {
    if (!lockedUntil) return null;
    const ms = new Date(lockedUntil).getTime() - Date.now();
    if (ms <= 0) return "expiring";
    const mins = Math.max(1, Math.ceil(ms / 60000));
    return `${mins} min`;
  };

  return (
    <div
      className="bg-white border border-[#E4E4E7] rounded-lg p-6"
      data-testid="admin-locked-accounts-card"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div className="flex items-center gap-2">
          <ShieldAlert
            className={`w-4 h-4 ${lockedCount > 0 ? "text-[#B91C1C]" : "text-[#71717A]"}`}
            strokeWidth={1.5}
          />
          <div className="mono-tiny text-[#71717A]">
            Locked accounts ({lockedCount} active · {sorted.length} tracked)
          </div>
        </div>
        <div className="mono-tiny text-[#71717A]">
          5 failures → 15 min lock
        </div>
      </div>

      {sorted.length === 0 ? (
        <div className="text-sm text-[#71717A] py-3" data-testid="admin-locked-empty">
          No suspicious login activity. Nothing to review.
        </div>
      ) : (
        <div className="divide-y divide-[#E4E4E7]">
          {sorted.map((row) => {
            const remaining = row.currently_locked
              ? formatRemaining(row.locked_until)
              : null;
            return (
              <div
                key={row.email}
                className="py-3 flex flex-col md:flex-row md:items-center md:justify-between gap-3"
                data-testid={`admin-locked-row-${row.email}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className="font-mono text-sm break-all"
                      data-testid={`admin-locked-email-${row.email}`}
                    >
                      {row.email}
                    </span>
                    {row.currently_locked ? (
                      <span
                        className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border bg-[#FEE2E2] text-[#B91C1C] border-[#EF4444]/30 inline-flex items-center gap-1"
                        data-testid={`admin-locked-pill-${row.email}`}
                      >
                        <Lock className="w-3 h-3" strokeWidth={2} />
                        LOCKED
                        {remaining ? ` · ${remaining}` : ""}
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border bg-[#FEF3C7] text-[#92400E] border-[#F59E0B]/30">
                        WATCHING
                      </span>
                    )}
                  </div>
                  <div className="mono-tiny text-[#71717A] mt-1">
                    <span data-testid={`admin-locked-count-${row.email}`}>
                      {row.count} failed attempt{row.count === 1 ? "" : "s"}
                    </span>
                    <span className="mx-1.5">·</span>
                    <span data-testid={`admin-locked-ip-${row.email}`}>
                      Last IP: {row.last_ip || "unknown"}
                    </span>
                    {row.ip_history && row.ip_history.length > 1 ? (
                      <span
                        className="ml-1 text-[#B91C1C]"
                        title={`Recent IPs: ${row.ip_history.join(", ")}`}
                      >
                        (+{row.ip_history.length - 1} more)
                      </span>
                    ) : null}
                  </div>
                  <div className="mono-tiny text-[#71717A]">
                    Last attempt:{" "}
                    {row.last_attempt_at
                      ? new Date(row.last_attempt_at).toLocaleString()
                      : "—"}
                    {row.currently_locked && row.locked_until ? (
                      <>
                        <span className="mx-1.5">·</span>
                        <span data-testid={`admin-locked-expires-${row.email}`}>
                          Lock expires:{" "}
                          {new Date(row.locked_until).toLocaleString()}
                        </span>
                      </>
                    ) : null}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant={row.currently_locked ? "default" : "outline"}
                  onClick={() => onUnlock(row.email)}
                  disabled={unlockingEmail === row.email}
                  className={
                    row.currently_locked
                      ? "bg-[#0F8F4F] hover:bg-[#0A6B3B] w-full md:w-auto"
                      : "w-full md:w-auto"
                  }
                  data-testid={`admin-unlock-btn-${row.email}`}
                >
                  {unlockingEmail === row.email ? (
                    <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                  ) : (
                    <Unlock className="w-3.5 h-3.5 mr-1.5" />
                  )}
                  Unlock email
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


function tierShort(label) {
  if (!label) return "—";
  // "Tier 2 · 200–499 cars · $0.90/car (10% off)" → "Tier 2"
  const m = /^Tier\s*\d+/i.exec(label);
  return m ? m[0] : label;
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function LeadsCard({
  rows,
  filter,
  onFilter,
  onToggle,
  onFollowUp,
  onResetReply,
  onQuickReply,
  onExport,
  updatingId,
  exporting,
}) {
  const [composerOpenId, setComposerOpenId] = useState(null);
  const counts = {
    all: rows.length,
    new: rows.filter((r) => (r.status || "new") === "new" && !r.replied).length,
    contacted: rows.filter(
      (r) => r.status === "contacted" && !r.replied
    ).length,
    replied: rows.filter((r) => r.replied).length,
  };
  const filtered = rows.filter((r) => {
    if (filter === "all") return true;
    if (filter === "replied") return !!r.replied;
    // For 'new' / 'contacted', exclude replied so replies surface only in
    // their own bucket and don't get lost in the noise.
    if (r.replied) return false;
    return (r.status || "new") === filter;
  });

  const filterChip = (key, label) => (
    <button
      key={key}
      type="button"
      onClick={() => onFilter(key)}
      data-testid={`leads-filter-${key}`}
      className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${
        filter === key
          ? "bg-[#0F8F4F] text-white border-[#0F8F4F]"
          : "bg-white text-[#3F3F46] border-[#E4E4E7] hover:border-[#0F8F4F]/50"
      }`}
    >
      {label} <span className="opacity-70">· {counts[key]}</span>
    </button>
  );

  return (
    <div
      className="bg-white border border-[#E4E4E7] rounded-lg overflow-hidden"
      data-testid="admin-leads-card"
    >
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 p-5 border-b border-[#E4E4E7]">
        <div className="flex items-center gap-2 min-w-0">
          <Mailbox
            className="w-4 h-4 text-[#0F8F4F] shrink-0"
            strokeWidth={1.5}
          />
          <div className="mono-tiny text-[#71717A]">
            Pricing-calculator leads ({rows.length})
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {filterChip("all", "All")}
          {filterChip("new", "New")}
          {filterChip("contacted", "Contacted")}
          {filterChip("replied", "Replied")}
          <Button
            size="sm"
            onClick={onExport}
            disabled={exporting || rows.length === 0}
            className="bg-[#09090B] hover:bg-[#27272A] text-white text-xs h-8 ml-1"
            data-testid="leads-export-csv"
          >
            {exporting ? (
              <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
            ) : (
              <Download className="w-3.5 h-3.5 mr-1.5" />
            )}
            Export CSV
          </Button>
        </div>
      </div>
      {filtered.length === 0 ? (
        <div
          className="p-6 text-sm text-[#71717A]"
          data-testid="leads-empty-state"
        >
          {rows.length === 0
            ? "No leads yet. Visitors who submit the Landing pricing calculator will show up here."
            : `No ${filter} leads.`}
        </div>
      ) : (
        <div className="divide-y divide-[#E4E4E7]">
          {filtered.map((lead) => {
            const status = lead.status || "new";
            const isContacted = status === "contacted";
            const isReplied = !!lead.replied;
            const isUpdating = updatingId === lead.id;
            const composerOpen = composerOpenId === lead.id;
            return (
              <div
                key={lead.id}
                className={`${
                  isReplied ? "border-l-4 border-l-[#0F8F4F]" : ""
                }`}
                data-testid={`lead-row-wrap-${lead.id}`}
              >
                <div
                  className={`p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-3 md:gap-4 ${
                    isReplied ? "bg-[#0F8F4F]/5" : ""
                  }`}
                  data-testid={`lead-row-${lead.id}`}
                  data-replied={isReplied ? "true" : "false"}
                >
                {/* Status pills */}
                <div className="md:w-32 shrink-0 order-2 md:order-none flex flex-col gap-1 items-start">
                  {isReplied && (
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wider bg-[#0F8F4F] text-white"
                      data-testid={`lead-replied-pill-${lead.id}`}
                      title={`Replied ${formatDate(lead.replied_at)}${
                        (lead.reply_count || 0) > 1 ? ` · ${lead.reply_count} times` : ""
                      }`}
                    >
                      <Reply className="w-3 h-3" strokeWidth={2} />
                      Replied — needs response
                    </span>
                  )}
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wider ${
                      isContacted
                        ? "bg-[#E4E4E7] text-[#52525B] border border-[#D4D4D8]"
                        : "bg-[#0F8F4F]/10 text-[#0F8F4F] border border-[#0F8F4F]/30"
                    }`}
                    data-testid={`lead-status-${lead.id}`}
                  >
                    <CircleDot className="w-3 h-3" strokeWidth={2} />
                    {isContacted ? "Contacted" : "New"}
                  </span>
                </div>

                {/* Email + date */}
                <div className="flex-1 min-w-0 order-1 md:order-none">
                  <div className="flex items-center gap-2 min-w-0">
                    <Mail
                      className="w-3.5 h-3.5 text-[#71717A] shrink-0"
                      strokeWidth={1.75}
                    />
                    <a
                      href={`mailto:${lead.email}`}
                      className="font-mono text-sm font-medium text-[#09090B] hover:text-[#0F8F4F] truncate"
                      data-testid={`lead-email-${lead.id}`}
                    >
                      {lead.email}
                    </a>
                  </div>
                  <div
                    className="mt-1 text-xs text-[#71717A]"
                    data-testid={`lead-date-${lead.id}`}
                  >
                    {formatDate(lead.created_at)}
                    {lead.contacted_at && (
                      <span className="ml-2">
                        · Contacted {formatDate(lead.contacted_at)}
                      </span>
                    )}
                    {lead.follow_up_sent_at && (
                      <span
                        className="ml-2 inline-flex items-center gap-1 text-[#0F8F4F]"
                        data-testid={`lead-followup-sent-${lead.id}`}
                        title={`Follow-up sent ${formatDate(lead.follow_up_sent_at)}${
                          (lead.follow_up_count || 0) > 1
                            ? ` · ${lead.follow_up_count} times`
                            : ""
                        }`}
                      >
                        <Send className="w-3 h-3" strokeWidth={2} />
                        Follow-up sent
                        {(lead.follow_up_count || 0) > 1
                          ? ` ×${lead.follow_up_count}`
                          : ""}
                      </span>
                    )}
                  </div>
                  {isReplied && (
                    <div
                      className="mt-2 rounded-md bg-white border border-[#0F8F4F]/30 px-2.5 py-1.5 text-xs text-[#3F3F46] max-w-prose"
                      data-testid={`lead-reply-excerpt-${lead.id}`}
                    >
                      {lead.last_reply_subject && (
                        <div className="font-semibold text-[#09090B] truncate">
                          {lead.last_reply_subject}
                        </div>
                      )}
                      {lead.last_reply_excerpt && (
                        <div className="text-[#52525B] line-clamp-2">
                          {lead.last_reply_excerpt}
                        </div>
                      )}
                      <div className="mt-0.5 text-[10px] uppercase tracking-wider text-[#0F8F4F]">
                        Replied {formatDate(lead.replied_at)}
                        {(lead.reply_count || 0) > 1 ? ` · ×${lead.reply_count}` : ""}
                      </div>
                    </div>
                  )}
                </div>

                {/* Cars + tier + monthly */}
                <div className="flex flex-wrap items-center gap-2 md:gap-4 order-3 md:order-none">
                  <div
                    className="text-sm"
                    data-testid={`lead-cars-${lead.id}`}
                  >
                    <span className="font-semibold text-[#09090B]">
                      {(lead.cars ?? 0).toLocaleString()}
                    </span>
                    <span className="text-[#71717A]"> cars</span>
                  </div>
                  <div
                    className="text-xs text-[#3F3F46] bg-[#F4F4F5] border border-[#E4E4E7] px-2 py-0.5 rounded"
                    data-testid={`lead-tier-${lead.id}`}
                    title={lead.tier_label || ""}
                  >
                    {tierShort(lead.tier_label)}
                  </div>
                  <div
                    className="text-sm font-semibold text-[#0F8F4F] min-w-[78px] text-right"
                    data-testid={`lead-monthly-${lead.id}`}
                  >
                    {lead.quote_required
                      ? "Quote"
                      : `$${(lead.monthly_total ?? 0).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}/mo`}
                  </div>
                </div>

                {/* Actions */}
                <div className="md:w-64 shrink-0 order-4 md:order-none flex flex-col sm:flex-row md:flex-col gap-2">
                  {isReplied && (
                    <Button
                      size="sm"
                      onClick={() =>
                        setComposerOpenId(composerOpen ? null : lead.id)
                      }
                      disabled={isUpdating}
                      className="w-full text-xs h-9 bg-[#0F8F4F] hover:bg-[#0A6B3B]"
                      data-testid={`lead-quick-reply-open-${lead.id}`}
                    >
                      <Reply className="w-3.5 h-3.5 mr-1.5" />
                      {composerOpen ? "Close reply" : "Reply"}
                    </Button>
                  )}
                  <Button
                    size="sm"
                    onClick={() => onFollowUp(lead)}
                    disabled={isUpdating}
                    variant={isReplied ? "outline" : "default"}
                    className={
                      isReplied
                        ? "w-full text-xs h-9 border-[#E4E4E7]"
                        : "w-full text-xs h-9 bg-[#0F8F4F] hover:bg-[#0A6B3B]"
                    }
                    data-testid={`lead-followup-${lead.id}`}
                  >
                    {isUpdating ? (
                      <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                    ) : (
                      <Send className="w-3.5 h-3.5 mr-1.5" />
                    )}
                    {(lead.follow_up_count || 0) > 0
                      ? "Resend follow-up"
                      : "Send follow-up"}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onToggle(lead)}
                    disabled={isUpdating}
                    className="w-full text-xs h-9 border-[#E4E4E7]"
                    data-testid={`lead-toggle-${lead.id}`}
                  >
                    {isContacted ? (
                      <Pencil className="w-3.5 h-3.5 mr-1.5" />
                    ) : (
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
                    )}
                    {isContacted ? "Reset to new" : "Mark contacted"}
                  </Button>
                  {isReplied && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onResetReply(lead)}
                      disabled={isUpdating}
                      className="w-full text-xs h-8 text-[#71717A] hover:text-[#09090B]"
                      data-testid={`lead-reset-reply-${lead.id}`}
                    >
                      <RotateCcw className="w-3 h-3 mr-1.5" />
                      Clear reply flag
                    </Button>
                  )}
                </div>
                </div>
                {isReplied && composerOpen && (
                  <QuickReplyComposer
                    lead={lead}
                    isSending={isUpdating}
                    onCancel={() => setComposerOpenId(null)}
                    onSend={async (payload) => {
                      const ok = await onQuickReply(lead, payload);
                      if (ok) setComposerOpenId(null);
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


const QUICK_REPLY_TEMPLATES = {
  schedule_call: {
    label: "Schedule call",
    body:
      "Hi,\n\nThanks for your reply. Would you like to schedule a quick " +
      "15-minute call to walk through ScanMint?\n\nBest,\nScanMint Team",
  },
  send_demo: {
    label: "Send demo",
    body:
      "Hi,\n\nHere's a quick demo of how ScanMint works:\n[insert demo link]" +
      "\n\nLet me know if you have any questions.\n\nBest,\nScanMint Team",
  },
  follow_up: {
    label: "Follow-up reminder",
    body:
      "Hi,\n\nJust following up on your interest in ScanMint. Let me know " +
      "if you'd like to move forward or have any questions.\n\nBest,\nScanMint Team",
  },
};

function QuickReplyComposer({ lead, isSending, onSend, onCancel }) {
  const [template, setTemplate] = useState("schedule_call");
  const [body, setBody] = useState(QUICK_REPLY_TEMPLATES.schedule_call.body);
  const [touched, setTouched] = useState(false);

  const pickTemplate = (key) => {
    setTemplate(key);
    if (!touched) {
      setBody(QUICK_REPLY_TEMPLATES[key].body);
    }
  };

  const resetToTemplate = () => {
    setBody(QUICK_REPLY_TEMPLATES[template].body);
    setTouched(false);
  };

  const handleSend = (e) => {
    e.preventDefault();
    const trimmed = body.trim();
    if (!trimmed) {
      toast.error("Reply body cannot be empty");
      return;
    }
    onSend({ template, body: trimmed });
  };

  return (
    <div
      className="px-4 md:px-5 pb-5 -mt-1"
      data-testid={`lead-quick-reply-composer-${lead.id}`}
    >
      <form
        onSubmit={handleSend}
        className="rounded-lg border border-[#E4E4E7] bg-white p-4 md:p-5 space-y-3"
      >
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="mono-tiny text-[#71717A]">
            Reply to{" "}
            <span className="text-[#09090B] font-medium">{lead.email}</span>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={isSending}
            className="text-[#71717A] hover:text-[#09090B] transition-colors"
            aria-label="Close composer"
            data-testid={`lead-quick-reply-close-${lead.id}`}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {Object.entries(QUICK_REPLY_TEMPLATES).map(([key, def]) => (
            <button
              key={key}
              type="button"
              onClick={() => pickTemplate(key)}
              disabled={isSending}
              data-testid={`lead-quick-reply-template-${key}-${lead.id}`}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                template === key
                  ? "bg-[#0F8F4F] text-white border-[#0F8F4F]"
                  : "bg-white text-[#3F3F46] border-[#E4E4E7] hover:border-[#0F8F4F]/50 hover:text-[#0F8F4F]"
              }`}
            >
              {def.label}
            </button>
          ))}
          <button
            type="button"
            onClick={resetToTemplate}
            disabled={isSending}
            data-testid={`lead-quick-reply-reset-${lead.id}`}
            className="text-xs text-[#71717A] hover:text-[#09090B] ml-auto self-center"
          >
            <RotateCcw className="w-3 h-3 inline mr-1" /> Reset to template
          </button>
        </div>
        <Textarea
          value={body}
          onChange={(e) => {
            setBody(e.target.value);
            setTouched(true);
          }}
          rows={7}
          required
          disabled={isSending}
          data-testid={`lead-quick-reply-body-${lead.id}`}
          className="font-mono text-sm min-h-[160px]"
          placeholder="Write your reply…"
        />
        <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isSending}
            className="text-xs h-9 border-[#E4E4E7]"
            data-testid={`lead-quick-reply-cancel-${lead.id}`}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isSending}
            className="text-xs h-9 bg-[#0F8F4F] hover:bg-[#0A6B3B]"
            data-testid={`lead-quick-reply-send-${lead.id}`}
          >
            {isSending ? (
              <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
            ) : (
              <Send className="w-3.5 h-3.5 mr-1.5" />
            )}
            Send reply
          </Button>
        </div>
      </form>
    </div>
  );
}


function UserDetailModal({ open, data, loading, onClose, onMarkSetupPaid, onClearLateFee }) {  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="user-detail-modal">
        <DialogHeader>
          <DialogTitle>Account details</DialogTitle>
          <DialogDescription>
            Full profile including properties, invoices, and paid totals.
          </DialogDescription>
        </DialogHeader>
        {loading || !data ? (
          <div className="py-10 flex items-center justify-center text-[#71717A]">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
          </div>
        ) : (
          <div className="space-y-5">
            <div>
              <div className="mono-tiny text-[#71717A]">Contact</div>
              <div className="font-medium text-lg" data-testid="user-detail-name">{data.user.name}</div>
              <div className="mono-tiny">{data.user.email}</div>
              <div className="mt-1 mono-tiny text-[#71717A]">
                Role: <b className="text-[#09090B] capitalize">{(data.user.role || "").replace("_"," ")}</b>
                {data.user.created_at && ` · joined ${new Date(data.user.created_at).toLocaleDateString()}`}
              </div>
              {data.user.last_login_at && (
                <div className="mono-tiny text-[#71717A]">
                  Last login: {new Date(data.user.last_login_at).toLocaleString()}
                </div>
              )}
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded border border-[#E4E4E7] p-3">
                <div className="mono-tiny text-[#71717A]">Properties</div>
                <div className="font-display text-2xl font-bold">{data.totals.properties_count}</div>
              </div>
              <div className="rounded border border-[#E4E4E7] p-3">
                <div className="mono-tiny text-[#71717A]">Invoices</div>
                <div className="font-display text-2xl font-bold">{data.totals.invoices_count}</div>
              </div>
              <div className="rounded border border-[#0F8F4F]/40 p-3 bg-[#0F8F4F]/5">
                <div className="mono-tiny text-[#71717A]">Paid total</div>
                <div className="font-display text-2xl font-bold text-[#0F8F4F]" data-testid="user-detail-paid-total">
                  ${data.totals.paid_amount.toFixed(2)}
                </div>
              </div>
            </div>

            {data.properties.length > 0 && (
              <div>
                <div className="mono-tiny text-[#71717A] mb-2">Properties</div>
                <div className="space-y-2">
                  {data.properties.map((p) => (
                    <div key={p.id} className="rounded border border-[#E4E4E7] p-3">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div>
                          <div className="font-medium">{p.property_name}</div>
                          <div className="mono-tiny text-[#71717A]">
                            {p.corporation_name} · {(p.billing_mode || "invoice")}
                            {p.is_demo ? " · DEMO" : ""}
                          </div>
                        </div>
                        <div className="mono-tiny flex items-center gap-3">
                          {p.setup_fee_paid_at ? (
                            <span className="text-[#0F8F4F]">Setup paid</span>
                          ) : (
                            <>
                              <span className="text-[#B45309]">Setup unpaid</span>
                              {onMarkSetupPaid && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 text-xs border-[#0F8F4F]/40 text-[#0F8F4F] hover:bg-[#0F8F4F]/5"
                                  onClick={() => onMarkSetupPaid(p.id)}
                                  data-testid={`admin-mark-setup-paid-${p.id}`}
                                >
                                  Mark paid
                                </Button>
                              )}
                            </>
                          )}
                          {p.billing_suspended_at && <span className="text-[#DC2626]">Suspended</span>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {data.invoices.length > 0 && (
              <div>
                <div className="mono-tiny text-[#71717A] mb-2">
                  Recent invoices ({data.invoices.length})
                </div>
                <div className="max-h-64 overflow-y-auto rounded border border-[#E4E4E7] divide-y divide-[#E4E4E7]">
                  {data.invoices.slice(0, 20).map((i) => {
                    // Detect "stuck" late fee: total > setup+monthly
                    // but no late_fee_amount recorded. Clear-late-fee
                    // resets total to exactly setup+monthly.
                    const base =
                      (parseFloat(i.setup_fee || 0) || 0) +
                      (parseFloat(i.monthly_subtotal || 0) || 0);
                    const hasLateFee =
                      Boolean(i.late_fee_amount) ||
                      (base > 0 && parseFloat(i.total || 0) > base + 0.01);
                    return (
                      <div key={i.id} className="p-3 flex items-center justify-between text-sm gap-2">
                        <div className="min-w-0">
                          <div className="font-mono text-xs truncate">{i.invoice_number}</div>
                          <div className="mono-tiny text-[#71717A]">
                            Due {(i.due_date || "").slice(0, 10)}
                            {i.late_fee_amount ? ` · +$${i.late_fee_amount} late fee` : ""}
                          </div>
                        </div>
                        <div className="text-right shrink-0 flex items-center gap-2">
                          <div>
                            <div className="font-semibold">${(i.total ?? 0).toFixed(2)}</div>
                            <div className={`mono-tiny uppercase ${
                              i.status === "paid" ? "text-[#0F8F4F]"
                              : i.status === "overdue" ? "text-[#DC2626]"
                              : "text-[#71717A]"
                            }`}>{i.status}</div>
                          </div>
                          {hasLateFee && onClearLateFee && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs"
                              onClick={() => onClearLateFee(i.id)}
                              data-testid={`admin-clear-late-fee-${i.id}`}
                              title={`Reset total to $${base.toFixed(2)}`}
                            >
                              Clear late fee
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function PurgeTestDataDialog({ preview, confirmText, setConfirmText, onConfirm, onCancel, busy }) {
  const c = preview?.would_delete || {};
  return (
    <Dialog open onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-lg" data-testid="purge-dialog">
        <DialogHeader>
          <DialogTitle>Purge test data</DialogTitle>
          <DialogDescription>
            Preview and confirm deletion of auto-generated test accounts.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded border border-[#F59E0B]/40 bg-[#FEF3C7] p-3 text-sm text-[#78350F]">
            This deletes every auto-generated test user AND cascades all of
            their properties, tenants, vehicles, invoices, notifications,
            and login history. <b>Irreversible.</b>
            <br />
            The <b>super-admin</b>, <b>manager@scanmint.com</b> and
            <b> scanner@scanmint.com</b> seeded accounts are always
            protected.
          </div>
          <div className="grid grid-cols-2 gap-2 text-sm">
            {Object.entries(c).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between rounded border border-[#E4E4E7] px-3 py-1.5" data-testid={`purge-count-${k}`}>
                <span className="capitalize text-[#71717A]">{k.replace("_", " ")}</span>
                <span className="font-mono font-bold">{v}</span>
              </div>
            ))}
          </div>
          {preview?.sample_emails?.length > 0 && (
            <details className="text-xs text-[#71717A]">
              <summary className="cursor-pointer">Sample of emails to delete ({preview.sample_emails.length})</summary>
              <ul className="mt-2 space-y-0.5 max-h-28 overflow-y-auto font-mono">
                {preview.sample_emails.map((e) => <li key={e}>{e}</li>)}
              </ul>
            </details>
          )}
          <div>
            <Label htmlFor="purge-confirm" className="text-xs">
              Type <b>PURGE</b> to confirm
            </Label>
            <Input
              id="purge-confirm"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="PURGE"
              disabled={busy}
              data-testid="purge-confirm-input"
              className="mt-1"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={busy}>Cancel</Button>
          <Button
            onClick={onConfirm}
            disabled={busy || confirmText !== "PURGE"}
            className="bg-[#DC2626] hover:bg-[#B91C1C]"
            data-testid="purge-confirm-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <AlertTriangle className="w-4 h-4 mr-2" />}
            Delete {c.users || 0} users + cascade
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function StatsDrillDownModal({ which, stats, users, suspended, invoices, onOpenUser, onClose }) {
  const titles = {
    users: `All users (${stats?.users ?? 0})`,
    properties: `All properties (${stats?.properties ?? 0})`,
    paid: `Paid accounts (${stats?.paid_accounts ?? 0})`,
    vehicles: `Vehicles (${stats?.vehicles ?? 0} · ${stats?.active_vehicles ?? 0} active)`,
    invoices: `Invoices (${stats?.paid_invoices ?? 0} paid · ${stats?.overdue_invoices ?? 0} overdue)`,
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid={`drilldown-${which}`}>
        <DialogHeader>
          <DialogTitle>{titles[which] || "Details"}</DialogTitle>
          <DialogDescription>
            Drill-down of the underlying records for this metric.
          </DialogDescription>
        </DialogHeader>
        {which === "users" && (
          <div className="divide-y divide-[#E4E4E7]">
            {users.map((u) => (
              <button
                key={u.id}
                type="button"
                onClick={() => { onClose(); onOpenUser(u.id); }}
                className="w-full py-2.5 px-2 -mx-2 rounded flex items-center justify-between text-left hover:bg-[#FAFAFA]"
              >
                <div className="min-w-0">
                  <div className="font-medium truncate">{u.name}</div>
                  <div className="mono-tiny text-[#71717A] truncate">{u.email}</div>
                </div>
                <div className="mono-tiny capitalize shrink-0 ml-3">
                  {(u.role || "").replace("_", " ")}
                </div>
              </button>
            ))}
          </div>
        )}
        {which === "paid" && (
          <div className="text-sm space-y-2">
            <div className="text-[#71717A]">
              Properties whose $99 setup fee has been paid.
            </div>
            <div className="rounded border border-[#0F8F4F]/40 bg-[#0F8F4F]/5 p-4 flex items-center justify-between">
              <span>Paid accounts</span>
              <span className="font-display text-2xl font-bold text-[#0F8F4F]">
                {stats?.paid_accounts ?? 0}
              </span>
            </div>
            <div className="rounded border border-[#E4E4E7] p-4 flex items-center justify-between">
              <span>Revenue collected (last 30 days)</span>
              <span className="font-display font-bold">
                ${(stats?.revenue_30d ?? 0).toFixed(2)}
              </span>
            </div>
          </div>
        )}
        {which === "properties" && (
          <div className="text-sm text-[#71717A]">
            {(suspended || []).length > 0
              ? `${suspended.length} currently suspended — see the Suspended properties card on the main dashboard.`
              : "Detailed property list — click a user row above to drill into their properties."}
          </div>
        )}
        {which === "vehicles" && (
          <div className="text-sm text-[#71717A]">
            {stats?.active_vehicles ?? 0} active · {(stats?.vehicles ?? 0) - (stats?.active_vehicles ?? 0)} inactive/suspended.
            Click a user row to see their vehicles broken out by status.
          </div>
        )}
        {which === "invoices" && (
          <div className="divide-y divide-[#E4E4E7]">
            {(invoices || []).slice(0, 40).map((i) => (
              <div key={i.id} className="py-2.5 flex items-center justify-between">
                <div className="min-w-0">
                  <div className="font-mono text-xs truncate">{i.invoice_number}</div>
                  <div className="mono-tiny text-[#71717A] truncate">{i.property_name}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-semibold">${(i.total ?? 0).toFixed(2)}</div>
                  <div className={`mono-tiny uppercase ${
                    i.status === "paid" ? "text-[#0F8F4F]"
                    : i.status === "overdue" ? "text-[#DC2626]"
                    : "text-[#71717A]"
                  }`}>{i.status}</div>
                </div>
              </div>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

